import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { JerseyColor } from '../../types/Player';

interface JerseyUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (newNumber: string, newColor: JerseyColor) => void;
  currentNumber: string;
  currentColor?: JerseyColor;
}

const JerseyUpdateModal: React.FC<JerseyUpdateModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  currentNumber,
  currentColor,
}) => {
  const [input, setInput] = useState(currentNumber);
  const [color, setColor] = useState<JerseyColor | undefined>(currentColor);

  useEffect(() => {
    setInput(currentNumber);
    setColor(currentColor);
  }, [currentNumber, currentColor, isOpen]);

  const numberValid = input.length > 0;
  const colorValid = color === 'red' || color === 'blue';
  const changed = input !== currentNumber || color !== currentColor;
  const canSubmit = numberValid && colorValid && changed;

  const handleSubmit = () => {
    if (canSubmit && color) {
      onUpdate(input, color);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Update Jersey</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mb-4">
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value.replace(/\D/g, '').slice(0, 3))}
            maxLength={3}
            inputMode="numeric"
            pattern="[0-9]*"
            className="text-3xl font-bold tracking-wider font-mono text-center w-full"
          />
        </div>
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Jersey Color</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setColor('red')}
              aria-pressed={color === 'red'}
              className={`rounded-lg py-3 font-semibold transition border-2 ${
                color === 'red'
                  ? 'bg-red-600 text-white border-red-700'
                  : 'bg-white text-red-600 border-red-200 hover:border-red-400'
              }`}
            >
              Red
            </button>
            <button
              type="button"
              onClick={() => setColor('blue')}
              aria-pressed={color === 'blue'}
              className={`rounded-lg py-3 font-semibold transition border-2 ${
                color === 'blue'
                  ? 'bg-blue-600 text-white border-blue-700'
                  : 'bg-white text-blue-600 border-blue-200 hover:border-blue-400'
              }`}
            >
              Blue
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 rounded-lg py-3 font-semibold transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`rounded-lg py-3 font-semibold transition ${
              canSubmit
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-200 text-white cursor-not-allowed'
            }`}
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default JerseyUpdateModal;
